import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { NotificationDropdown } from './NotificationDropdown';
import AIFeedbackButton from './AIFeedbackButton';
import ReminderBell from './ReminderBell';
import { enablePush, currentPushPermission, fcmConfigured } from '../lib/push';
import { setBadge } from '../lib/notifyBridge';
import { NAV_GROUPS } from '../config/navConfig';
import {
  LayoutDashboard, LogOut,
  ChevronLeft, Menu, Bell, User, ChevronRight, ChevronDown,
  Sun, Moon,
  ShoppingCart, Boxes, IndianRupee, BarChart3,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DARK_LOGO  = 'https://customer-assets.emergentagent.com/job_inventory-core-11/artifacts/43j2lsb0_Tirth-Logo-Final-File-01.png';
const LIGHT_LOGO = 'https://customer-assets.emergentagent.com/job_inventory-core-11/artifacts/6wta179t_Tirth-Logo-Final-File-02.png';

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const lastCountRef = useRef(null);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Persist which groups are expanded across navigations
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('ti_nav_groups');
      if (raw) return JSON.parse(raw);
    } catch {}
    return { overview: true, sales: true, production: true };
  });
  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('ti_nav_groups', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Fetch unread notification count every 30s; toast on increase + sync the
  // OS-level app badge (`navigator.setAppBadge`). When the badge isn't
  // supported the call is a no-op — desktop Chrome/Edge + installed PWA
  // on Android will show the dot/numeric badge on the home-screen icon.
  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      const newCount = res.data.count || 0;
      if (lastCountRef.current !== null && newCount > lastCountRef.current) {
        const delta = newCount - lastCountRef.current;
        toast(`${delta} new notification${delta > 1 ? 's' : ''}`, { description: 'Tap the bell to view', duration: 4000 });
      }
      lastCountRef.current = newCount;
      setNotifCount(newCount);
      setBadge(newCount);   // ← OS app-icon badge
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !fcmConfigured) return;
    if (currentPushPermission() === 'granted') {
      // Wire up foreground push listener — displays real OS notifications
      // via the service worker (desktop notification centre / Android shade),
      // not in-app toasts. See /app/frontend/src/lib/push.js.
      enablePush().catch(() => {});
    }
  }, [user]);

  // Filter visible items — uses centralized perm catalog
  const itemVisible = (item) => {
    // Owner/admin always sees everything
    if (user?.role === 'owner' || user?.role === 'admin') return true;
    // Client portal users — sidebar is replaced entirely; here just hide internal pages
    if (user?.role === 'client') return false;
    // Admin-only items require admin role
    if (item.adminOnly) return false;
    // Permission-gated items: respect explicit perm flag (closed-by-default)
    if (item.perm) return Boolean(user?.permissions?.[item.perm]);
    // Items without a perm key are visible to all authenticated users
    return true;
  };

  // Auto-open the group containing the active route
  useEffect(() => {
    for (const g of NAV_GROUPS) {
      if (g.items.some(it => it.path === location.pathname)) {
        setOpenGroups(prev => prev[g.id] ? prev : { ...prev, [g.id]: true });
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const logoSrc = theme === 'light' ? LIGHT_LOGO : DARK_LOGO;

  // Preserve sidebar scroll position across navigations (was resetting to top
  // because SidebarContent is recreated on every render).
  const navScrollRef = useRef(0);
  const navElRef = useRef(null);
  const setNavRef = (el) => {
    navElRef.current = el;
    if (el && navScrollRef.current) {
      el.scrollTop = navScrollRef.current;
    }
  };
  const saveNavScroll = (e) => { navScrollRef.current = e.currentTarget.scrollTop; };
  // Also restore after every render (route change)
  useEffect(() => {
    if (navElRef.current) navElRef.current.scrollTop = navScrollRef.current;
  });

  // Current-page label (scan flat list)
  const currentLabel = (() => {
    for (const g of NAV_GROUPS) {
      const hit = g.items.find(it => it.path === location.pathname);
      if (hit) return hit.label;
    }
    return location.pathname.startsWith('/analytics/') ? 'Analytics' : 'ERP';
  })();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-3 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <img
          src={logoSrc}
          alt="Tirth Industries"
          className={`rounded-lg object-contain flex-shrink-0 ${collapsed ? 'w-9 h-9' : 'w-10 h-10'}`}
          style={{ background: 'transparent' }}
        />
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-heading font-bold text-foreground text-sm leading-tight truncate">Tirth Industries</div>
            <div className="text-muted-foreground text-xs">ERP System</div>
          </div>
        )}
      </div>

      {/* Nav — grouped */}
      <nav ref={setNavRef} onScroll={saveNavScroll} className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {NAV_GROUPS.map(group => {
          const visibleItems = group.items.filter(itemVisible);
          if (visibleItems.length === 0) return null;
          const isOpen = collapsed ? true : !!openGroups[group.id];
          const GroupIcon = group.icon;
          const hasActive = visibleItems.some(it => it.path === location.pathname);
          // Per-group palette — fades into amber default if a future group
          // forgets to declare one. Operators have repeatedly asked to
          // identify modules by colour, not just label.
          const palette = group.color || { accent: 'text-amber-400', bg: 'bg-amber-500/10', ring: 'border-amber-400', sectionLabel: 'text-amber-500 dark:text-amber-400' };
          return (
            <div key={group.id}>
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`nav-group-${group.id}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                    hasActive ? palette.accent : `${palette.sectionLabel} hover:text-foreground`
                  }`}
                >
                  <GroupIcon size={12} className="opacity-80" />
                  <span>{group.label}</span>
                  <ChevronDown size={12} className={`ml-auto transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                </button>
              )}
              {isOpen && (
                <div className={`${collapsed ? '' : 'pl-1'} space-y-0.5`}>
                  {visibleItems.map(({ path, label, icon: Icon }) => {
                    const active = location.pathname === path;
                    return (
                      <button
                        key={path}
                        data-testid={`nav-${label.toLowerCase().replace(/\s+|\&/g, '-')}`}
                        onClick={() => { navigate(path); setMobileOpen(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 group border-l-2 ${
                          active
                            ? `${palette.bg} ${palette.accent} ${palette.ring}`
                            : 'border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground'
                        }`}
                      >
                        <Icon size={15} className={active ? palette.accent : 'opacity-80 group-hover:opacity-100'} />
                        {!collapsed && <span className="truncate">{label}</span>}
                        {!collapsed && active && <ChevronRight size={13} className={`ml-auto ${palette.accent}`} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="px-2 py-3 border-t border-border space-y-1">
        <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <User size={13} className="text-primary" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-foreground text-xs font-medium truncate">{user?.name}</div>
              <div className="text-muted-foreground text-xs truncate">{user?.email}</div>
            </div>
          )}
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-destructive transition-colors text-sm ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={15} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col bg-background border-r border-border transition-all duration-300 flex-shrink-0 relative ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <SidebarContent />
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute top-1/2 -translate-y-1/2 -right-2.5 hidden md:flex w-5 h-10 items-center justify-center bg-secondary hover:bg-accent border border-border rounded-r-md text-muted-foreground z-10 transition-colors"
        >
          <ChevronLeft size={12} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="relative flex flex-col w-60 bg-background border-r border-border z-10 h-full">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-4 md:px-6 py-3 bg-background border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-secondary"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={18} />
            </button>
            <div className="text-foreground font-heading font-semibold text-sm">
              {currentLabel}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AIFeedbackButton inHeader />
            <ReminderBell />
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-primary transition-colors"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="relative">
              <button
                data-testid="notifications-bell"
                onClick={() => setNotifOpen(v => !v)}
                className="relative p-2 rounded-lg text-muted-foreground hover:bg-secondary transition-colors"
              >
                <Bell size={16} />
                {notifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-destructive text-white text-[9px] font-bold flex items-center justify-center" data-testid="notif-badge">
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </button>
              <NotificationDropdown
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                onCountChange={fetchCount}
              />
            </div>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-card rounded-lg border border-border">
              <div className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                <span className="text-primary text-xs font-bold">{user?.name?.[0] || 'A'}</span>
              </div>
              <span className="text-foreground text-xs font-medium">{user?.name?.split(' ')[0]}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background pb-24 md:pb-0" data-testid="main-content">
          <div className="p-4 md:p-6">
            {children}
          </div>
        </main>
      </div>
      {/* AIFeedbackButton is now mounted inside the header, next to the theme toggle. */}

      {/* Mobile Bottom Navigation — extra bottom padding so the Made-With-Emergent
          widget doesn't overlap the labels */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[9999] bg-card/95 backdrop-blur-sm border-t border-border flex justify-around items-start pt-1.5 pb-5 h-[72px] px-2 safe-area-pb">
        {[
          { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
          { path: '/sales', icon: ShoppingCart, label: 'Sales' },
          { path: '/inventory', icon: Boxes, label: 'Stock' },
          { path: '/cashflow', icon: IndianRupee, label: 'Cash' },
          { path: '/reports', icon: BarChart3, label: 'Reports' },
        ].map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <button key={path} onClick={() => navigate(path)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${active ? 'text-primary' : 'text-muted-foreground'}`}>
              <Icon size={20} />
              <span className="text-[9px] font-medium">{label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
